<template>
  <span :class="badgeClasses" :data-variant="props.variant">
    <slot />
  </span>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'

/** Theme-based variants (design system); category-style variants use Tailwind soft tint (bg-*-500/15 text-*-400). */
export type BaseBadgeVariant =
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'purple'
  | 'reminder'
  | 'gray'
  | 'secondary'
  | 'primary'
  | 'slate'
  | 'cyan'
  | 'amber'
  | 'pink'
  | 'rose'
  | 'indigo'
  | 'violet'
  | 'teal'
  | 'emerald'
  | 'orange'
  | 'lime'
  | 'sky'
  | 'fuchsia'
  | 'stone'
  | 'zinc'
  | 'neutral'
  | 'custom'
  | 'warning'
  | 'danger'
  | 'sku'
  | 'sku-discontinued'

interface Props {
  variant?: BaseBadgeVariant | (string & {})
  /** TRANSITIONAL (DD-DESIGN-001): dd theme uses .dd-pages token styling for queue pills. */
  theme?: 'default' | 'dd'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'slate',
  theme: 'default',
})

const defaultBaseClasses = 'inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full'

const skuBaseClasses =
  'inline-block font-[family-name:var(--sd-f-mono)] text-[11px] font-semibold px-[6px] py-[2px] rounded-[var(--sd-r-1,6px)]'

// Theme-based variants (existing design system)
const themeVariantClasses: Record<string, string> = {
  red: 'bg-badge-red text-badge-red',
  blue: 'bg-badge-blue text-badge-blue',
  green: 'bg-badge-green text-badge-green',
  yellow: 'bg-badge-yellow text-badge-yellow',
  purple: 'bg-badge-purple text-badge-purple',
  reminder: 'bg-badge-reminder text-badge-reminder',
  gray: 'bg-badge-gray text-badge-gray',
  secondary: 'bg-badge-secondary text-badge-secondary',
  primary: 'bg-primary text-primary-foreground'
}

// Category-style variants (Tailwind soft tint: bg-*-500/15 text-*-400)
const categoryVariantClasses: Record<string, string> = {
  slate: 'bg-slate-500/15 text-slate-400',
  cyan: 'bg-cyan-500/15 text-cyan-400',
  amber: 'bg-amber-500/15 text-amber-400',
  pink: 'bg-pink-500/15 text-pink-400',
  rose: 'bg-rose-500/15 text-rose-400',
  indigo: 'bg-indigo-500/15 text-indigo-400',
  violet: 'bg-violet-500/15 text-violet-400',
  teal: 'bg-teal-500/15 text-teal-400',
  emerald: 'bg-emerald-500/15 text-emerald-400',
  orange: 'bg-orange-500/15 text-orange-400',
  lime: 'bg-lime-500/15 text-lime-400',
  sky: 'bg-sky-500/15 text-sky-400',
  fuchsia: 'bg-fuchsia-500/15 text-fuchsia-400',
  stone: 'bg-stone-500/15 text-stone-400',
  zinc: 'bg-zinc-500/15 text-zinc-400',
  neutral: 'bg-neutral-500/15 text-neutral-400',
  custom: ''
}

const skuVariantClasses: Record<string, string> = {
  sku: 'text-[color:var(--sd-accent)] bg-[color:color-mix(in_oklab,var(--sd-accent),transparent_90%)]',
  'sku-discontinued':
    'text-[color:var(--sd-bad)] bg-[color:color-mix(in_oklab,var(--sd-bad),transparent_90%)]',
}

const ddBaseClasses =
  'inline-flex items-center justify-center leading-none font-[family-name:var(--dd-f-mono)] font-bold'

const ddVariantClasses: Record<string, string> = {
  'complexity-low':
    'text-[9px] px-[5px] py-[2px] rounded-[4px] bg-[color:var(--dd-good-bg)] text-[color:var(--dd-good)]',
  'complexity-med':
    'text-[9px] px-[5px] py-[2px] rounded-[4px] bg-[color:var(--dd-warn-bg)] text-[color:var(--dd-warn)]',
  'complexity-high':
    'text-[9px] px-[5px] py-[2px] rounded-[4px] bg-[color:var(--dd-bad-bg)] text-[color:var(--dd-bad)]',
  path:
    'text-[9.5px] font-semibold px-2 py-[3px] rounded-full bg-[color:var(--dd-badge-neutral-bg)] text-[color:var(--dd-badge-neutral-text)] border border-[color:var(--dd-line)] tracking-[0.06em]',
  'priority-vip':
    'text-[9.5px] px-[7px] py-[2px] rounded-full bg-[color:var(--dd-bad-bg)] text-[color:var(--dd-bad)] tracking-[0.05em]',
  'priority-standard':
    'text-[9.5px] px-[7px] py-[2px] rounded-full bg-[color:var(--dd-badge-neutral-bg)] text-[color:var(--dd-badge-neutral-text)] tracking-[0.05em]',
  std:
    'text-[8.5px] font-bold px-[6px] py-[2px] rounded-full bg-[color:var(--dd-std-bg)] text-[color:var(--dd-std-text)] border border-[color:var(--dd-std-border)] tracking-[0.07em]',
}

function isSkuVariant(variant: string): boolean {
  return variant === 'sku' || variant === 'sku-discontinued'
}

// Map variant to classes (design: rounded-full pill, px-2.5 py-1, text-xs font-medium)
// Parent can pass class for layout (e.g. flex, gap); variant color is always applied.
const attrs = useAttrs()
const badgeClasses = computed(() => {
  const passed = attrs.class
  const passedStr =
    typeof passed === 'string'
      ? passed
      : Array.isArray(passed)
        ? passed.join(' ')
        : passed
          ? String(passed)
          : ''
  const v = props.variant

  if (props.theme === 'dd') {
    const ddClass = ddVariantClasses[v] ?? ddVariantClasses.path
    return [ddBaseClasses, ddClass, passedStr].filter(Boolean).join(' ')
  }

  if (isSkuVariant(v)) {
    const skuClass = skuVariantClasses[v] ?? skuVariantClasses.sku
    return [skuBaseClasses, skuClass, passedStr].filter(Boolean).join(' ')
  }

  const themeClass = themeVariantClasses[v]
  const categoryClass = categoryVariantClasses[v]
  // 'custom': no built-in color so parent :class (e.g. category style map) applies
  const resolved =
    v === 'custom' ? '' : (themeClass ?? categoryClass ?? categoryVariantClasses.slate)
  return [defaultBaseClasses, resolved, passedStr].filter(Boolean).join(' ')
})
</script>
